import Modal from '@/components/shared/ModalV1/Modal.component';
import Typography from '@/components/shared/typography/typography.component';
import myImageLoader from '@/image.local.loader';
import { CarDetailsType } from '@/types';
import { numberWithCommas } from '@/utils';
import { TFunction } from 'i18next';
import Image from 'next/image';
import {
    CalendarHalfGreenCheckedIcon,
    CloseIcon,
    DashedGrayLine,
    InspectionPointsIcon,
    LegalitiesCheckedIcon,
    NoAccidentsIcon,
    NotTemperedIcon,
    ZeroRepairCostIcon,
} from 'public/Images';
import { FC } from 'react';

const ItemList: FC<{
    text: string;
    value: number | string;
    t: TFunction<'translation', undefined>;
}> = ({ text, value, t }) => {
    return (
        <div className="flex justify-between items-center">
            <div className="flex gap-1.5 items-center px-2">
                <div className="w-[3px] h-[3px] bg-gray-500 rounded-full" />
                <Typography variant="caption" className="!text-gray-500">
                    {text}
                </Typography>
            </div>
            {typeof value === 'string' ? (
                <Typography variant="caption" className="!text-primary-600">
                    {value}
                </Typography>
            ) : (
                <Typography variant="caption" className="!text-primary-600">
                    {t('EGP')} {numberWithCommas(+value)}
                </Typography>
            )}
        </div>
    );
};
const BenefitItem: FC<{ benefit: string; icon: string }> = ({
    benefit,
    icon,
}) => {
    return (
        <div className="flex gap-2.5 items-center w-[184px]">
            <div className="w-14 h-14 bg-white rounded-xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.08)] flex items-center justify-center">
                <Image src={icon} alt="icon" />
            </div>
            <Typography className="!text-[10px] !text-gray-800 font-semibold">
                {benefit}
            </Typography>
        </div>
    );
};

const PriceBreakdownModal: FC<{
    carDetails: CarDetailsType;
    openPriceBreakdownModal: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    t: TFunction<'translation', undefined>;
}> = ({ carDetails, openPriceBreakdownModal, setOpen, t }) => {
    const carDisplayName = `${carDetails.make} ${carDetails.model} ${carDetails.year}`;
    return (
        <Modal
            open={openPriceBreakdownModal}
            setOpen={setOpen}
            className="!rounded-xl"
        >
            <div className="px-3 h-full w-[680px] bg-[#FFF] bg-gradient-to-b from-[#F3FFFA] via-[#F3FFFA00]/[32%]">
                {/* Header */}
                <div className="flex gap-2 pl-2 py-4 items-center w-full">
                    <div className="w-[53px] h-10 ">
                        <img
                            className="rounded"
                            src={myImageLoader({
                                src: carDetails.images[0],
                                width: 200,
                                height: 150,
                                quality: 80,
                            })}
                            alt="car image"
                        />
                    </div>
                    <div className="flex flex-col">
                        <Typography
                            variant="body2SemiBold"
                            className="!text-gray-800"
                        >
                            {t('priceBenefits')}
                        </Typography>
                        <Typography
                            variant="captionMedium"
                            className="!text-gray-600"
                        >
                            {carDisplayName}
                        </Typography>
                    </div>
                    <Image
                        onClick={() => {
                            setOpen(false);
                        }}
                        src={CloseIcon}
                        alt="close"
                        className="en:ml-auto ar:mr-auto self-start cursor-pointer"
                    />
                </div>
                <div className="p-3 flex flex-col gap-6">
                    {/* First Container */}
                    <div className="py-4 px-3 rounded-xl border border-gray-200 bg-white">
                        <div className="flex justify-between mb-3">
                            <Typography
                                variant="body2Medium"
                                className="!text-gray-700"
                            >
                                {t('baseCarPrice')}
                            </Typography>
                            <Typography
                                variant="body2Medium"
                                className="!text-gray-800"
                            >
                                {t('EGP')}{' '}
                                {numberWithCommas(
                                    carDetails.basePrice +
                                        carDetails.warrantyPrice || 0
                                )}
                            </Typography>
                        </div>
                        <div className="flex flex-col gap-2 pr-2">
                            <ItemList
                                text={t('ownershipFees')}
                                value={carDetails.registrationFees || 0}
                                t={t}
                            />
                            <ItemList
                                text={t('warrantyDays')}
                                value={t('included')}
                                t={t}
                            />
                            <ItemList
                                text={t('sylndrFees')}
                                value={10000}
                                t={t}
                            />
                            <ItemList
                                text={t('carRenewalIncluded')}
                                value={t('included')}
                                t={t}
                            />
                        </div>
                        <Image
                            src={DashedGrayLine}
                            alt="dashed-line"
                            className="w-full my-4"
                        />
                        <div className="flex justify-between items-center">
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {t('currentPrice')}
                            </Typography>
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {t('EGP')}{' '}
                                {numberWithCommas(+carDetails.displayingPrice)}
                            </Typography>
                        </div>
                    </div>
                    {/* Second Container */}
                    <div className="flex flex-col gap-4">
                        <div className="w-full flex items-center justify-center">
                            <Typography
                                variant="body2Medium"
                                className="!text-gray800
                                "
                            >
                                {t('whyBuyFromSylndr')}
                            </Typography>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="flex px-6 gap-6 justify-between items-center">
                                <BenefitItem
                                    benefit={t('inspectionPoints')}
                                    icon={InspectionPointsIcon}
                                />
                                <BenefitItem
                                    benefit={t('returnPolicyDays')}
                                    icon={CalendarHalfGreenCheckedIcon}
                                />
                                <BenefitItem
                                    benefit={t('zeroRepairCost')}
                                    icon={ZeroRepairCostIcon}
                                />
                            </div>
                            <div className="flex px-6 gap-6 justify-between items-center">
                                <BenefitItem
                                    benefit={t('nonMajorAccidents')}
                                    icon={NoAccidentsIcon}
                                />
                                <BenefitItem
                                    benefit={t('nonTampered')}
                                    icon={NotTemperedIcon}
                                />
                                <BenefitItem
                                    benefit={t('legalitiesChecked')}
                                    icon={LegalitiesCheckedIcon}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PriceBreakdownModal;
